import axios from "axios"
import { toast } from 'sonner'
import CryptoBridge from './CryptoBridge'
import { DEFAULT_API_ENCRYPTION, API_ENCRYPTION_MODES } from './api_constants'
import { GET_USER_PUBLIC_KEY, UPLOAD_MULTIPART_FILE } from './url_helper'

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL
const APPLICATION_ACCESS_KEY = process.env.REACT_APP_APPLICATION_ACCESS_KEY
const DEFAULT_TIMEOUT = 30000
export const TOKEN_REFRESH_LOCK_MS = 60000

const isTokenRefreshLockActive = () => {
    const lock = localStorage.getItem("tokenRefreshLock")
    if (!lock) return false
    const lockTime = parseInt(lock, 10)
    return !isNaN(lockTime) && Date.now() - lockTime < TOKEN_REFRESH_LOCK_MS
}

// In-memory cache for user's public key (not persisted to localStorage)
let cachedPublicKey = null
// Holds the in-flight fetch promise so parallel requests share one network call
let inFlightPublicKeyFetch = null

// Endpoints that must always use NONE encryption regardless of global default
const NONE_ENCRYPTION_ENDPOINTS = [
    GET_USER_PUBLIC_KEY,
    UPLOAD_MULTIPART_FILE,
]

const encryptionHandlers = {
    [API_ENCRYPTION_MODES.NONE]: data => data,
    [API_ENCRYPTION_MODES.BASE64]: {
        encrypt: data => {
            if (typeof data === "object") {
                return btoa(JSON.stringify(data))
            }
            return btoa(data)
        },
        decrypt: data => {
            try {
                const decoded = atob(data)
                try {
                    return JSON.parse(decoded)
                } catch (e) {
                    return decoded
                }
            } catch (e) {
                console.error("BASE64 decryption failed", e)
                return data
            }
        },
    },
    [API_ENCRYPTION_MODES.PKI]: {
        encrypt: (data, publicKey) => {
            if (!publicKey) {
                throw new Error('Public key is required for PKI encryption')
            }

            try {
                const crypto = new CryptoBridge(publicKey, 'PUBLIC_KEY')
                const jsonString = typeof data === 'object'
                    ? JSON.stringify(data)
                    : data
                return crypto.encryptData(jsonString)
            } catch (error) {
                console.error('PKI encryption failed:', error)
                throw error
            }
        },
        decrypt: (data, publicKey) => {
            if (!publicKey) {
                throw new Error('Public key is required for PKI decryption')
            }

            try {
                const crypto = new CryptoBridge(publicKey, 'PUBLIC_KEY')
                const decrypted = crypto.decryptData(data)

                try {
                    return JSON.parse(decrypted)
                } catch (e) {
                    return decrypted
                }
            } catch (error) {
                console.error('PKI decryption failed:', error)
                throw error
            }
        },
    },
}

class ApiClient {
    constructor(options = {}) {
        this.baseURL = options.baseURL || API_BASE_URL
        this.timeout = options.timeout || DEFAULT_TIMEOUT
        this.applicationAccessKey =
            options.applicationAccessKey || APPLICATION_ACCESS_KEY
        this.sessionToken = options.sessionToken || localStorage.getItem("_atk") || ""
        this.defaultEncDecAlgo = options.encDecAlgo || DEFAULT_API_ENCRYPTION

        this.instance = axios.create({
            baseURL: this.baseURL,
            timeout: this.timeout,
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
            validateStatus: function (status) {
                return true
            },
        })

        this.instance.interceptors.request.use(
            this._handleRequest.bind(this),
            this._handleRequestError.bind(this)
        )

        this.instance.interceptors.response.use(this._handleResponse.bind(this))
    }

    setAuth(sessionToken) {
        this.sessionToken = sessionToken
        return this
    }

    setEncryptionAlgorithm(algorithm) {
        if (Object.values(API_ENCRYPTION_MODES).includes(algorithm)) {
            this.defaultEncDecAlgo = algorithm
        } else {
            console.warn(
                `Unsupported encryption algorithm: ${algorithm}. Using ${DEFAULT_API_ENCRYPTION}.`
            )
            this.defaultEncDecAlgo = DEFAULT_API_ENCRYPTION
        }
        return this
    }

    async _handleRequest(config) {
        if (this.applicationAccessKey) {
            config.headers["application-access-key"] = this.applicationAccessKey
        }

        if (this.sessionToken) {
            config.headers["session-token"] = this.sessionToken
        }

        // if (config.keyword) {
        //   config.headers["keyword"] = config.keyword
        // }
        if (!config.bypassRefreshLock && isTokenRefreshLockActive()) {
            console.log('[API CLIENT] Blocking request during token refresh:', config.url)
            return Promise.reject({ tokenRefreshInProgress: true })
        }

        const isOpenWebEndpoint = typeof config.url === "string" && config.url.includes("/openWeb/")
        const isNoneEndpoint = typeof config.url === "string" && NONE_ENCRYPTION_ENDPOINTS.some(ep => config.url.includes(ep))

        config.headers["enc-dec-algo"] =
            config.encDecAlgo ||
            (isNoneEndpoint ? API_ENCRYPTION_MODES.NONE : isOpenWebEndpoint ? API_ENCRYPTION_MODES.BASE64 : this.defaultEncDecAlgo)

        if (config.pagination) {
            const { startIndex = 0, recordCount = 10 } = config.pagination
            config.headers["start-index"] = startIndex.toString()
            config.headers["record-count"] = recordCount.toString()
        }

        const encDecAlgo = config.headers["enc-dec-algo"]

        // Fetch public key if PKI encryption is requested
        let publicKey = null
        if (encDecAlgo === API_ENCRYPTION_MODES.PKI) {
            publicKey = await this._fetchPublicKey()
        }
        console.log("Public Key fetched for PKI:", publicKey)
        if (config.method === "get") {
            if (encDecAlgo !== API_ENCRYPTION_MODES.NONE && config.headers["input-parameters"]) {
                try {
                    const encryptHandler = encryptionHandlers[encDecAlgo]
                    if (encryptHandler && encryptHandler.encrypt) {
                        config.headers["input-parameters"] = encryptHandler.encrypt(
                            config.headers["input-parameters"],
                            publicKey
                        )
                    }
                } catch (error) {
                    console.error("Encryption error:", error)
                }
            } else if (config.headers["input-parameters"]) {
                config.headers["input-parameters"] = JSON.stringify(
                    config.headers["input-parameters"]
                )
            }
        } else {
            // Skip inputParameters wrapping for FormData (multipart uploads)
            if (config.data instanceof FormData) {
                // Keep FormData as-is for multipart uploads
                // Remove Content-Type header to let browser set it with boundary
                delete config.headers['Content-Type']
            } else if (encDecAlgo !== API_ENCRYPTION_MODES.NONE && config.data) {
                try {
                    const encryptHandler = encryptionHandlers[encDecAlgo]
                    if (encryptHandler && encryptHandler.encrypt) {
                        config.data = {
                            inputParameters: encryptHandler.encrypt(config.data, publicKey),
                        }
                    }
                } catch (error) {
                    console.error("Encryption error:", error)
                }
            } else if (config.data) {
                config.data = { inputParameters: config.data }
            }
        }

        return config
    }

    _handleUnauthorized() {
        this.sessionToken = ""
        localStorage.removeItem("_atk")
        localStorage.removeItem("tokenRefreshLock")

        cachedPublicKey = null
        inFlightPublicKeyFetch = null

        window.location.href = "/login"
    }

    _fetchPublicKey() {
        if (cachedPublicKey) {
            return Promise.resolve(cachedPublicKey)
        }

        if (!this.sessionToken) {
            return Promise.reject(new Error('Session token is required to fetch public key'))
        }

        if (inFlightPublicKeyFetch) {
            return inFlightPublicKeyFetch
        }

        inFlightPublicKeyFetch = this.instance.request({
            method: 'get',
            url: GET_USER_PUBLIC_KEY,
            headers: {
                'application-access-key': this.applicationAccessKey,
                'session-token': this.sessionToken,
                'accept': 'application/json',
            }
        })
            .then(response => {
                const publicKey = response?.publicKey
                if (!publicKey) throw new Error('Public key not found in response')
                cachedPublicKey = publicKey
                return publicKey
            })
            .catch(error => {
                console.error('Failed to fetch public key:', error)
                throw new Error('Could not fetch public key for PKI encryption')
            })
            .finally(() => {
                inFlightPublicKeyFetch = null
            })

        return inFlightPublicKeyFetch
    }

    _handleRequestError(error) {
        return Promise.reject(error)
    }

    async _handleResponse(response) {
        const { data, config } = response
        if (!data) {
            return Promise.reject(new Error("No data received"))
        }

        const {
            timeStamp,
            requestID,
            resource,
            status,
            description,
            data: responseData,
            processingSpan,
        } = data

        switch (status) {
            case 200: {
                const encDecAlgo = config.headers["enc-dec-algo"]

                if (encDecAlgo === API_ENCRYPTION_MODES.NONE) {
                    return responseData
                } else if (encDecAlgo === API_ENCRYPTION_MODES.BASE64) {
                    const decryptHandler = encryptionHandlers[encDecAlgo]
                    if (
                        typeof responseData === "string" &&
                        decryptHandler &&
                        decryptHandler.decrypt
                    ) {
                        return decryptHandler.decrypt(responseData)
                    }
                    return responseData
                } else if (encDecAlgo === API_ENCRYPTION_MODES.PKI) {
                    const decryptHandler = encryptionHandlers[API_ENCRYPTION_MODES.PKI]
                    if (
                        typeof responseData === "string" &&
                        decryptHandler &&
                        decryptHandler.decrypt
                    ) {
                        // Use cached public key for decryption
                        return decryptHandler.decrypt(responseData, cachedPublicKey)
                    }
                    return responseData
                }
                return responseData
            }

            case 400: {
                const encDecAlgo = config.headers["enc-dec-algo"]
                let errorMessage

                if (encDecAlgo === API_ENCRYPTION_MODES.NONE) {
                    errorMessage = responseData
                } else if (encDecAlgo === API_ENCRYPTION_MODES.BASE64) {
                    const decryptHandler = encryptionHandlers[API_ENCRYPTION_MODES.BASE64]
                    if (
                        typeof responseData === "string" &&
                        decryptHandler &&
                        decryptHandler.decrypt
                    ) {
                        errorMessage = decryptHandler.decrypt(responseData)
                    } else {
                        errorMessage = responseData
                    }
                } else if (encDecAlgo === API_ENCRYPTION_MODES.PKI) {
                    const decryptHandler = encryptionHandlers[API_ENCRYPTION_MODES.PKI]
                    if (
                        typeof responseData === "string" &&
                        decryptHandler &&
                        decryptHandler.decrypt
                    ) {
                        errorMessage = decryptHandler.decrypt(responseData, cachedPublicKey)
                    } else {
                        errorMessage = responseData
                    }
                }

                if (errorMessage === "NO_CONTENT") {
                    errorMessage = "No content available"
                }

                // Show toast only for POST requests
                if (resource && resource.startsWith("POST")) {
                    const toastMessage = errorMessage || "Bad Request"
                    toast.error(toastMessage)
                }

                return Promise.reject({
                    status,
                    description,
                    message: errorMessage,

                    requestID,
                    timestamp: timeStamp,
                })
            }

            case 500: {
                if (description === "401 UNAUTHORIZED") {
                    const tokenUsedInRequest = config.headers?.["session-token"]
                    const currentToken = this.sessionToken || localStorage.getItem("_atk")
                    const skipRedirect = isTokenRefreshLockActive() ||
                        (tokenUsedInRequest && currentToken && tokenUsedInRequest !== currentToken)
                    if (skipRedirect) {
                        console.log('[API CLIENT] 401 - skipping redirect (refresh in progress or token changed)')
                        return Promise.reject({
                            status,
                            description,
                            stackTrace: responseData,
                            requestID,
                            timestamp: timeStamp,
                            tokenRefreshInProgress: true,
                        })
                    }
                    console.warn("Unauthorized access - redirecting to login", {
                        status,
                        description,
                        stackTrace: responseData,
                        requestID,
                        timestamp: timeStamp,
                    })
                    this._handleUnauthorized()
                } else {
                    // Show error toast for all 500 errors (except 401 unauthorized)
                    const errorMessage = `${description || "Internal Server Error"}${responseData ? `: ${responseData}` : ""}`
                    toast.error(errorMessage, {
                        className: 'toast-500-error',
                        style: {
                            minWidth: '600px',
                            maxWidth: '800px',
                            width: 'auto',
                            maxHeight: '80vh',
                            overflowY: 'auto',
                            display: 'block',
                            paddingRight: '40px',
                            paddingTop: '16px',
                            paddingBottom: '16px',
                            paddingLeft: '16px',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            lineHeight: '1.5'
                        }
                    })
                }
                return Promise.reject({
                    status,
                    description,
                    stackTrace: responseData,
                    requestID,
                    timestamp: timeStamp,
                })
            }

            default:
                return Promise.reject({
                    status,
                    description,
                    data: responseData,
                    requestID,
                    timestamp: timeStamp,
                })
        }
    }

    request(config) {
        return this.instance.request(config)
    }

    /**
     * GET method
     */
    get(url, params = {}, options = {}) {
        console.log('[API_CLIENT] get called with options:', options)
        return this.request({
            method: "get",
            url,
            params,
            ...options,
        })
    }

    /**
     * POST method
     */
    post(url, data = {}, options = {}) {
        return this.request({
            method: "post",
            url,
            data,
            ...options,
        })
    }
}

const apiClientInstance = new ApiClient()

export default apiClientInstance

export { ApiClient }
